import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Box, Typography, Grid, Paper, Avatar, Stack
} from '@mui/material';
import ButtonBase from '@mui/material/ButtonBase';
import LandingPageNav from '../../components/landingpage/LandingPageNav';
import Footer from '../../components/Footer/footer';
import FloatingBranchProfile from './FloatingBranchProfile';
import { apigetPublic } from '../../service/api';
import { BranchPageSkeleton } from '../../components/skeletons/PageSkeletons';

export default function BranchPage() {
  const { branchName } = useParams();

  const [branchDetails, setBranchDetails] = useState(null);
  const [officers, setOfficers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const loadBranchPage = async () => {
      setLoading(true);
      setNotFound(false);

      // Fetch branches and loan officers in parallel: one round trip, not two.
      const [branchRes, loRes] = await Promise.all([
        apigetPublic('branches/search'),
        apigetPublic('loanofficer/search'),
      ]);
      if (cancelled) return;

      if (branchRes?.status !== 200) {
        setNotFound(true);
        setLoading(false);
        return;
      }

      const branchList = branchRes.data.result || [];

      // Find branch by slug OR name
      const branch = branchList.find(
        (b) =>
          b.slug?.toLowerCase() === branchName.toLowerCase() ||
          b.name?.toLowerCase() === branchName.toLowerCase()
      );

      if (!branch) {
        console.warn('Branch not found:', branchName);
        setNotFound(true);
        setLoading(false);
        return;
      }

      // Assign branch details directly
      setBranchDetails({
        id: branch._id,
        name: branch.name,
        slug: branch.slug,
        city: branch.city,
        stateCode: branch.stateCode,
        zip: branch.zip,
        phone: branch.phone,
        email: branch.email,
        address: branch.address,
        image: branch.branchImageUrl,
        nmls: branch.nmls,
        preQualificationLink: branch.preQualificationLink,
      });

      // ----------------------------------
      // 2. FILTER LOAN OFFICERS BY BRANCH
      // ----------------------------------
      const allOfficers = loRes?.status === 200 ? loRes.data.result || [] : [];

      // Officers can match by:
      // - branch name
      // - branch slug
      // - branch ID (if your LO model stores it)
     const filtered = allOfficers.filter((officer) =>
  officer.branches?.some((b) => {
    const bSlugOrName =
      (typeof b === 'string' ? b : (b?.slug || b?.name || b?._id || ''));

    const bLower = String(bSlugOrName).toLowerCase();

    return (
      bLower === branch.name.toLowerCase() ||
      bLower === branch.slug.toLowerCase() ||
      bLower === String(branch._id).toLowerCase()
    );
  })
);


      setOfficers(filtered);
      setLoading(false);
    };

    loadBranchPage();

    return () => {
      cancelled = true;
    };
  }, [branchName]);

  return (
    <>
      <LandingPageNav />

     {/* Floating branch header */}
{branchDetails && (
  <FloatingBranchProfile
    city={branchDetails.city}
    phone={branchDetails.phone}
    email={branchDetails.email}
     nmls={branchDetails.nmls}
    preQualificationLink={branchDetails.preQualificationLink}
    address={[
      branchDetails.address,
     [branchDetails.city, branchDetails.stateCode, branchDetails.zip].filter(Boolean).join(' '),
    ]
      .filter(Boolean)
      .join(', ')}
  />
)}


      <Box sx={{ px: 4, pt: 12, pb: 6, maxWidth: 1200, mx: 'auto' }}>
        {/* LOADING — page frame with placeholders */}
        {loading && <BranchPageSkeleton />}

        {/* NOT FOUND */}
        {!loading && notFound && (
          <Box sx={{ textAlign: 'center', py: 10 }}>
            <Typography variant="h2" component="h1" gutterBottom>
              Branch not found
            </Typography>
            <Typography color="text.secondary">
              We couldn&rsquo;t find a branch matching &ldquo;{branchName}&rdquo;.{' '}
              <Link to="/find-a-loan-officer?tab=branches">Browse all branches</Link>.
            </Typography>
          </Box>
        )}

        {/* ------------------------------- */}
        {/* Branch Hero Section */}
        {/* ------------------------------- */}
        {!loading && branchDetails && (
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              height: { xs: 300, md: 450 },
              mb: 6,
              overflow: 'hidden',
            }}
          >
            <Box
              component="img"
              src={branchDetails.image}
              alt={branchDetails.name}
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                position: 'absolute',
                top: 0,
                left: 0,
                zIndex: 1,
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                background:
                  'linear-gradient(to right, rgba(0,0,0,0.7), rgba(0,0,0,0.2))',
                zIndex: 2,
              }}
            />
            <Box
              sx={{
                position: 'relative',
                zIndex: 3,
                height: '100%',
                px: { xs: 2, md: 10 },
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                color: 'white',
                flexDirection: { xs: 'column', md: 'row' },
                textAlign: { xs: 'center', md: 'left' },
              }}
            >
              <Box sx={{ maxWidth: 400 }}>
                <Typography variant="heroHeadline" mb={1}>
                  {branchDetails.name}
                </Typography>
                <Typography>{branchDetails.phone}</Typography>
              <Typography>
                  {[
                    branchDetails.address,
                    [branchDetails.city, branchDetails.stateCode, branchDetails.zip].filter(Boolean).join(' '),
                  ].filter(Boolean).join(', ')}
                </Typography>
              </Box>

              <Typography variant="heroSubheadline" sx={{ textShadow: '0 0 8px rgba(0,0,0,0.4)' }}>
                Meet Your Local Team
              </Typography>
            </Box>
          </Box>
        )}

        {/* ------------------------------- */}
        {/* Description Section */}
        {/* ------------------------------- */}
       {!loading && !notFound && (
       <>
       <Box sx={{ mt: 4, mb: 6 }}>
  <Typography variant="body1" fontWeight="bold" sx={{ mb: 2 }}>
    Your home is one of the most important purchases you'll ever make. With so many decisions to consider, it's essential to have a lending partner you trust by your side.
  </Typography>
  <Typography variant="body1">
    At Jet HomeLoans, we specialize in financing new construction homes in communities across the Southeast. Our experienced team is here to help you explore your options and find the perfect loan solution for your needs. We’re excited to guide you on your journey to homeownership.
  </Typography>
</Box> 

        {/* ------------------------------- */}
        {/* Loan Officers List */}
        {/* ------------------------------- */}
        <Typography variant="h2" gutterBottom>
          Meet Your Local Team
        </Typography>

        <Grid container spacing={4}>
          {officers.map((officer, idx) => (
            <Grid item xs={12} md={4} key={idx}>
              <ButtonBase
                component={Link}
                to={`/${officer.slug}`}
                sx={{ width: '100%', textAlign: 'left' }}
              >
                <Paper sx={{ p: 3, width: '100%' }}>
                  <Stack alignItems="center" spacing={2}>
                    <Avatar
                      sx={{ width: 80, height: 80 }}
                      src={
                        officer.imageUrl?.startsWith('http')
                          ? officer.imageUrl
                          : officer.imageUrl
                          ? `https://${officer.imageUrl}`
                          : `/assets/images/avatars/avatar_${(idx % 24) + 1}.jpg`
                      }
                      alt={`${officer.firstName} ${officer.lastName}`}
                    />

                    <Typography variant="h4" component="h3">
                      {officer.firstName} {officer.lastName}
                    </Typography>
                    <Typography variant="body2">{officer.title}</Typography>
                    <Typography variant="body2">{officer.phone}</Typography>
                    <Typography variant="body2">{officer.email}</Typography>
                  </Stack>
                </Paper>
              </ButtonBase>
            </Grid>
          ))}
        </Grid>

        {officers.length === 0 && (
          <Typography color="text.secondary">
            No loan officers are listed for this branch yet.
          </Typography>
        )}
       </>
       )}
      </Box>
      <Footer />
    </>
  );
}
